import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { useSiteContent } from '@/context/SiteContentContext';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { supabase } from '@/integrations/pms/compat';

/**
 * Custom Design mode header — a clean, minimal header distinct from
 * the Hydra CMS-themed headers. Flat design with simple navigation.
 */
export const CustomHeader: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const ctx = useSiteContent();

  const { data: bizSettings } = useQuery({
    queryKey: ['business-settings-header'],
    queryFn: async () => {
      const { data } = await supabase
        .from('business_settings')
        .select('logo_url, company_name, phone')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const companyName = bizSettings?.company_name || ctx.template.brand?.name || 'Company';
  const logoUrl = bizSettings?.logo_url || null;
  const phone = bizSettings?.phone || ctx.template.contact?.phone || '';

  const navLinks = [
    { label: 'Services', href: '/services' },
    { label: 'About', href: '/about' },
    { label: 'Service Areas', href: '/service-areas' },
    { label: 'Resources', href: '/resources' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className="h-8 w-auto object-contain" />
            ) : (
              <span className="text-lg font-semibold text-gray-900">{companyName}</span>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.href
                    ? 'text-gray-900'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {phone && (
              <a
                href={`tel:${phone.replace(/\D/g, '')}`}
                className="hidden sm:flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {phone}
              </a>
            )}
            <Link
              to="/contact"
              className="hidden md:inline-flex px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
            >
              Get Quote
            </Link>
            <button
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="block px-2 py-2 text-sm font-medium text-white bg-gray-900 rounded-md text-center mt-2"
              onClick={() => setMobileOpen(false)}
            >
              Get Quote
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};
